import { IQuoteLineItem, IQuoteProduct, QuoteTerm } from "../../shared/types";
import { quoteProductTerm } from "../QuoteLineItemGrid/quoteLineItemUtils";
import { EditorSection, newEditorSection, sectionDisplayName } from "../QuoteSectionBar/sectionUtils";
import { lineIsComplete } from "../../pages/CreateQuotePage/createQuoteUtils";

// Issue 52: Copy Term - copy every line of one term in a section into one or more other terms,
// in the same section, another section, or a new section, so staff can offer a term comparison
// without re-entering the products. The copies are ordinary unsaved lines: the editor's existing
// PreviewPrices, promotion check, draft autosave and Save handle them like any added line.

export const quoteTerms: QuoteTerm[] = ["P1M", "P1YM", "P1YA"];
const maxSectionNameLength = 200;

export type CopyTermDestination = { kind: "section"; index: number } | { kind: "new" };

export type TermCopyResult = {
  term: QuoteTerm;
  // The new lines, already moved to `term`.
  copies: IQuoteLineItem[];
  // The source lines (not the copies) that land in each warning group.
  notOffered: IQuoteLineItem[];
  alreadyPresent: IQuoteLineItem[];
  manualBlanked: IQuoteLineItem[];
  promoDropped: IQuoteLineItem[];
};

export type TermCopyPlan = {
  sourceIndex: number;
  sourceTerm: QuoteTerm;
  destination: CopyTermDestination;
  byTerm: TermCopyResult[];
  // Lines in the source term that aren't complete yet (no product, no price, ...) - never copied.
  incompleteCount: number;
  copyCount: number;
};

// The terms the section has at least one complete line in, in P1M/P1YM/P1YA order.
export const termsInSection = (section: EditorSection | undefined): QuoteTerm[] => quoteTerms.filter(term =>
  (section?.items || []).some(item => item.term_duration === term && lineIsComplete(item)));

const lineKey = (item: Pick<IQuoteLineItem, "product_id" | "sku_id" | "segment">) =>
  `${item.product_id}\u0000${item.sku_id}\u0000${item.segment}`;

// A custom product is priced by hand, so it exists in every term. A catalog product exists in a
// term when the catalog has its product/SKU/segment at that term - the same lookup the grid's
// own pricing uses.
export const isOfferedInTerm = (item: IQuoteLineItem, term: QuoteTerm, productRows: IQuoteProduct[]) =>
  item.product_id.startsWith("custom:") || productRows.some(row => row.product_id === item.product_id
    && row.sku_id === item.sku_id && row.segment === item.segment && quoteProductTerm(row.term_duration, row.billing_plan) === term);

// The line moved to `term`. Everything that belonged to the source line's term or to a saved row is
// dropped: its identity, every server-computed price and proration output, the renewal date of the
// subscription it may have been imported from, and its promotion (promotions are term-specific; the
// grid's background promotion check looks again at the new term). A manual line keeps its Discount
// but loses its MSRP/Cost, which were entered for the old term - Save stays blocked until staff
// enter the new term's prices. Product, segment, quantity, note and any own dates are kept.
export const copyLineToTerm = (item: IQuoteLineItem, term: QuoteTerm): IQuoteLineItem => ({
  ...item,
  quote_line_item_id: undefined, sort_order: undefined,
  term_duration: term,
  renewal_date: null,
  msrp_unit_price: null, sale_unit_price: null, cost_unit_price: null, total_price: null, margin: null,
  raw_msrp_unit_price: undefined, raw_sale_unit_price: undefined, raw_cost_unit_price: undefined,
  applied_discount_pct: undefined, adjustment_reason: undefined, is_commission_eligible: undefined,
  coterm_verified_at: undefined, charge_start_date: undefined, charge_end_date: undefined,
  billable_days: undefined, full_period_days: undefined, proration_detail: undefined,
  promo_source: null, promo_id: null, microsoft_promo_pct: null, customer_promo_pct: null,
  promo_end_date: null, promo_min_quantity: null, promo_max_quantity: null, promo_eligibility: null, promo_eligibility_detail: null,
  promo_name: null, promo_prerequisite_skus: null, promo_prerequisite_status: null, promo_rejected: false,
  ...(item.is_price_manual ? { manual_msrp_unit_price: null, manual_cost_unit_price: null } : {}),
});

export const planTermCopy = ({ sections, sourceIndex, sourceTerm, targetTerms, destination, productRows }: {
  sections: EditorSection[];
  sourceIndex: number;
  sourceTerm: QuoteTerm;
  targetTerms: QuoteTerm[];
  destination: CopyTermDestination;
  productRows: IQuoteProduct[];
}): TermCopyPlan => {
  const sourceLines = (sections[sourceIndex]?.items || []).filter(item => item.term_duration === sourceTerm);
  const candidates = sourceLines.filter(lineIsComplete);
  const existing = destination.kind === "section" ? sections[destination.index]?.items || [] : [];
  const byTerm = quoteTerms.filter(term => term !== sourceTerm && targetTerms.includes(term)).map(term => {
    // A new section starts empty, one per term; an existing destination may already carry some of
    // these products at this term (including copies made earlier in this same run).
    const present = new Set(existing.filter(item => item.term_duration === term && item.product_id).map(lineKey));
    const result: TermCopyResult = { term, copies: [], notOffered: [], alreadyPresent: [], manualBlanked: [], promoDropped: [] };
    candidates.forEach(item => {
      if (!isOfferedInTerm(item, term, productRows)) { result.notOffered.push(item); return; }
      if (present.has(lineKey(item))) { result.alreadyPresent.push(item); return; }
      present.add(lineKey(item));
      result.copies.push(copyLineToTerm(item, term));
      if (item.is_price_manual) result.manualBlanked.push(item);
      if (item.promo_source) result.promoDropped.push(item);
    });
    return result;
  });
  return {
    sourceIndex, sourceTerm, destination, byTerm,
    incompleteCount: sourceLines.length - candidates.length,
    copyCount: byTerm.reduce((sum, result) => sum + result.copies.length, 0),
  };
};

// "Option 1 (P1YA)", or just "P1YA" when that would run past the section name limit - a section
// name is never truncated.
export const copiedSectionName = (sourceName: string, sourceIndex: number, term: QuoteTerm) => {
  const name = `${sectionDisplayName(sourceName, sourceIndex)} (${term})`;
  return name.length <= maxSectionNameLength ? name : term;
};

const lineCount = (count: number) => `${count} ${count === 1 ? "line" : "lines"}`;

// One sentence group per target term, including a term nothing was copied to, so the summary
// still says why (the dialog's preview already listed each line).
const termMessage = (result: TermCopyResult, destinationName: string | null) => {
  const parts = [result.copies.length && destinationName
    ? `Copied ${lineCount(result.copies.length)} to ${result.term} in ${destinationName}.`
    : `No lines copied to ${result.term}.`];
  if (result.notOffered.length) parts.push(`${lineCount(result.notOffered.length)} not offered in ${result.term} not copied.`);
  if (result.alreadyPresent.length) parts.push(`${lineCount(result.alreadyPresent.length)} already in ${result.term} not copied.`);
  if (result.manualBlanked.length) parts.push(`Enter the ${result.term} MSRP and Cost for ${lineCount(result.manualBlanked.length)}.`);
  return parts.join(" ");
};

// Applies a plan: appends the copies to the destination section (term by term, in source order),
// or adds one new section per term that actually has copies (an empty section would only block
// Save). Returns the section to show next - the destination - and a short summary.
export const applyTermCopy = (sections: EditorSection[], plan: TermCopyPlan) => {
  const source = sections[plan.sourceIndex];
  if (!source || plan.copyCount === 0) return { sections, activeIndex: plan.sourceIndex, message: "No lines were copied." };
  if (plan.destination.kind === "section") {
    const destinationIndex = plan.destination.index;
    const destinationName = sectionDisplayName(sections[destinationIndex]?.name, destinationIndex);
    return {
      sections: sections.map((section, index) => index === destinationIndex
        ? { ...section, items: [...section.items, ...plan.byTerm.flatMap(result => result.copies)] } : section),
      activeIndex: destinationIndex,
      message: plan.byTerm.map(result => termMessage(result, destinationName)).join(" "),
    };
  }
  const added = plan.byTerm.filter(result => result.copies.length > 0).map(result => ({
    ...newEditorSection(result.copies),
    name: copiedSectionName(source.name, plan.sourceIndex, result.term),
    dates: { ...source.dates },
  }));
  const names = new Map(plan.byTerm.filter(result => result.copies.length > 0).map((result, i) => [result.term, added[i].name]));
  return {
    sections: [...sections, ...added],
    activeIndex: sections.length,
    message: plan.byTerm.map(result => termMessage(result, names.get(result.term) ?? null)).join(" "),
  };
};
